import { ReactNode } from "react";
import { Check, Clock, Circle, Lock, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export type StepCardStatus = "done" | "in_progress" | "not_started" | "locked" | "optional";

interface StepCardProps {
  stepNumber: number | string;
  title: string;
  description?: string;
  status: StepCardStatus;
  statusDetail?: string;
  lockedReason?: string;
  hint?: string;
  children?: ReactNode;
}

const STATUS_CFG: Record<
  StepCardStatus,
  {
    Icon: typeof Check;
    label: string;
    badge: string;
    border: string;
    circle: string;
    glow: string;
  }
> = {
  done: {
    Icon: Check,
    label: "Gotowe",
    badge: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    border: "border-emerald-500/30",
    circle: "bg-emerald-500 text-white",
    glow: "",
  },
  in_progress: {
    Icon: Clock,
    label: "W toku",
    badge: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
    border: "border-l-4 border-l-violet-500 border-violet-500/30",
    circle: "bg-amber-500 text-white animate-pulse",
    glow: "shadow-[0_0_0_1px] shadow-violet-500/20",
  },
  not_started: {
    Icon: Circle,
    label: "Nie rozpoczęto",
    badge: "bg-zinc-500/15 text-zinc-600 dark:text-zinc-400 border-zinc-500/30",
    border: "border-border/60",
    circle: "bg-zinc-700 text-zinc-300",
    glow: "",
  },
  locked: {
    Icon: Lock,
    label: "Zablokowane",
    badge: "bg-slate-700/40 text-slate-400 border-slate-600/40",
    border: "border-dashed border-slate-700/50",
    circle: "bg-slate-700 text-slate-400",
    glow: "opacity-60",
  },
  optional: {
    Icon: AlertCircle,
    label: "Opcjonalne",
    badge: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
    border: "border-border/60 border-dashed",
    circle: "bg-sky-500 text-white",
    glow: "",
  },
};

export function StepCard({
  stepNumber,
  title,
  description,
  status,
  statusDetail,
  lockedReason,
  hint,
  children,
}: StepCardProps) {
  const cfg = STATUS_CFG[status];
  const Icon = cfg.Icon;
  const isLocked = status === "locked";

  return (
    <div
      className={`rounded-lg border ${cfg.border} ${cfg.glow} bg-card transition-all scroll-mt-4`}
      data-step-status={status}
      data-step-card={String(stepNumber)}
    >
      <div className="flex items-start gap-3 p-4">
        {/* Number circle */}
        <div
          className={`shrink-0 w-9 h-9 rounded-full ${cfg.circle} flex items-center justify-center font-bold text-sm shadow-sm`}
          aria-hidden="true"
        >
          {isLocked ? <Lock className="h-4 w-4" /> : stepNumber}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title row */}
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            <Badge
              className={`${cfg.badge} border text-[11px] gap-1 py-0.5 px-2`}
              variant="outline"
            >
              <Icon className="h-3 w-3" />
              {cfg.label}
              {statusDetail && <span className="opacity-80">· {statusDetail}</span>}
            </Badge>
          </div>

          {description && (
            <p className="text-xs text-muted-foreground leading-relaxed mb-2">{description}</p>
          )}

          {/* Locked reason (only when locked) */}
          {isLocked && lockedReason && (
            <div className="mt-2 rounded-md bg-slate-700/20 border border-slate-700/40 p-2.5">
              <p className="text-[11px] text-slate-400 leading-snug">
                <Lock className="h-3 w-3 inline mr-1" />
                {lockedReason}
              </p>
            </div>
          )}

          {/* Hint (info note) */}
          {hint && !isLocked && (
            <div className="mt-2 rounded-md bg-violet-500/5 border border-violet-500/20 p-2.5">
              <p className="text-[11px] text-violet-700 dark:text-violet-400 leading-snug">
                💡 {hint}
              </p>
            </div>
          )}

          {/* Children: actions, stats, sub-content */}
          {children && !isLocked && <div className="mt-3">{children}</div>}
        </div>
      </div>
    </div>
  );
}
